/**
 * /privacy (#727 MA-14 store prep): the privacy policy the Play Store and App Store listings link to
 * (https://hip4.bitbazaar.cc/privacy). It covers the website and the Android and iOS apps. Every statement must stay
 * true of the code: no analytics, no accounts, and (B7 #796) the only backend is the optional push-notification
 * service in backend/ (what it stores: backend/schema/tables.json, backend/src/db/repos.ts, backend/README.md
 * "Devices API"). The full text is mirrored in docs/PRIVACY.md (PrivacyPage.test.tsx keeps them equal); the apps'
 * in-app privacy text is mobile/android/docs/PRIVACY.md and mobile/ios/docs/PRIVACY.md. The store answers built on it
 * are in docs/STORE_SUBMISSION.md. If a feature starts collecting or sharing anything, change this page AND those
 * answers.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { t as tr } from '../i18n/i18n';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

/** TODO(#727): the legal publisher (person or company) is not decided yet; see docs/STORE_SUBMISSION.md. */
export const PRIVACY_PUBLISHER = '[Publisher name TBD]';
/** TODO(#727): a monitored contact address for privacy requests (store listings require one). */
export const PRIVACY_CONTACT = '[privacy contact email TBD]';
/** English date of the last update; the page shows privacy.updated_date (same date, written per language). */
export const PRIVACY_UPDATED = tr('privacy.updated_date');

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="space-y-2">
      <h2 id={`${id}-h`} className="text-lg font-semibold">
        {title}
      </h2>
      <div className="space-y-2 text-sm leading-relaxed text-fg/85">{children}</div>
    </section>
  );
}

const TODO = 'rounded bg-warn/15 px-1 font-medium text-warn';

export default function PrivacyPage() {
  const t = useT();
  return (
    <article className="mx-auto max-w-3xl space-y-6" data-testid="privacy-page">
      <header className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-accent">{t('common.privacy')}</p>
        <h1 className="text-2xl font-semibold sm:text-3xl">{t('privacy.title')}</h1>
        <p className="text-sm text-muted">
          <Msg k="privacy.updated" args={{ date: <span data-testid="privacy-updated">{t('privacy.updated_date')}</span> }} />
        </p>
      </header>

      <div className="card space-y-2 p-4 text-sm" data-testid="privacy-summary">
        <p className="font-semibold">{t('privacy.summary.lead')}</p>
        <ul className="list-disc space-y-1 ps-5 text-fg/85">
          <li>{t('privacy.summary.accounts')}</li>
          <li>{t('privacy.summary.tracking')}</li>
          <li>{t('privacy.summary.server')}</li>
          <li>{t('privacy.summary.notifications')}</li>
          <li>{t('privacy.summary.settings')}</li>
        </ul>
      </div>

      <Section id="who" title={t('privacy.who.title')}>
        <p>
          <Msg
            k="privacy.who.p1"
            args={{
              publisher: (
                <span className={TODO} data-testid="privacy-publisher" translate="no">
                  {PRIVACY_PUBLISHER}
                </span>
              ),
            }}
          />
        </p>
      </Section>

      <Section id="collect" title={t('privacy.collect.title')}>
        <p>{t('privacy.collect.p1')}</p>
      </Section>

      <Section id="notifications" title={t('privacy.notify.title')}>
        <p>{t('privacy.notify.p1')}</p>
        <ul className="list-disc space-y-1 ps-5">
          <li>{t('privacy.notify.token')}</li>
          <li>{t('privacy.notify.device')}</li>
          <li>{t('privacy.notify.address')}</li>
          <li>{t('privacy.notify.prefs')}</li>
        </ul>
        <p>{t('privacy.notify.p2')}</p>
        <p>{t('privacy.notify.p3')}</p>
        <p>{t('privacy.notify.p4')}</p>
        <p>
          {t('privacy.notify.basis')}{' '}
          <span className={TODO} translate="no">
            {/* Pre-publication note for the owner (#796), deliberately not in the catalog. */}
            TODO: counsel to confirm the legal basis and the controller (publisher) details.
          </span>
        </p>
      </Section>

      <Section id="retention" title={t('privacy.retention.title')}>
        <p>{t('privacy.retention.p1')}</p>
        <p>{t('privacy.retention.p2')}</p>
        <p>{t('privacy.retention.p3')}</p>
      </Section>

      <Section id="processors" title={t('privacy.processors.title')}>
        <p>
          {t('privacy.processors.p1')}{' '}
          <span className={TODO} translate="no">
            {/* Pre-publication note for the owner (#796), deliberately not in the catalog. */}
            TODO: counsel to name the transfer safeguard (for example standard contractual clauses) for EU/UK users.
          </span>
        </p>
      </Section>

      <Section id="rights" title={t('privacy.rights.title')}>
        <p>
          <Msg
            k="privacy.rights.p1"
            args={{
              contact: (
                <span className={TODO} translate="no">
                  {PRIVACY_CONTACT}
                </span>
              ),
            }}
          />
        </p>
      </Section>

      <Section id="hyperliquid" title={t('privacy.hl.title')}>
        <p>{t('privacy.hl.p1')}</p>
        <p>{t('privacy.hl.p2')}</p>
      </Section>

      <Section id="wallets" title={t('privacy.wallets.title')}>
        <p>{t('privacy.wallets.p1')}</p>
        <p>{t('privacy.wallets.p2')}</p>
      </Section>

      <Section id="device" title={t('privacy.device.title')}>
        <p>{t('privacy.device.p1')}</p>
        <ul className="list-disc space-y-1 ps-5">
          <li>{t('privacy.device.network')}</li>
          <li>{t('privacy.device.watch')}</li>
          <li>{t('privacy.device.tour')}</li>
          <li>{t('privacy.device.notify')}</li>
          <li>{t('privacy.device.agent')}</li>
        </ul>
        <p>{t('privacy.device.p2')}</p>
      </Section>

      <Section id="hosting" title={t('privacy.hosting.title')}>
        <p>
          {t('privacy.hosting.p1')}{' '}
          <span className={TODO} translate="no">
            {/* Pre-publication note for the owner (#727), deliberately not in the catalog. */}
            TODO: confirm the CloudFront access-log setting before publishing.
          </span>
        </p>
      </Section>

      <Section id="permissions" title={t('privacy.permissions.title')}>
        <p>{t('privacy.permissions.p1')}</p>
      </Section>

      <Section id="children" title={t('privacy.children.title')}>
        <p>{t('privacy.children.p1')}</p>
      </Section>

      <Section id="changes" title={t('privacy.changes.title')}>
        <p>
          <Msg
            k="privacy.changes.p1"
            args={{
              contact: (
                <span className={TODO} data-testid="privacy-contact" translate="no">
                  {PRIVACY_CONTACT}
                </span>
              ),
            }}
          />
        </p>
      </Section>

      <footer className="pb-4 text-xs text-muted">
        <Link to="/learn" className="text-accent hover:underline">
          {t('learn.title')}
        </Link>
        {' · '}
        <Link to="/" className="text-accent hover:underline">
          {t('common.back_to_markets')}
        </Link>
      </footer>
    </article>
  );
}
